/**
 * Msg 590 · Phase A — Module-level singleton cache for
 * `atlas_categories`. One fetch per session, shared across every
 * mounted tree/row/form. When any admin CRUD lands the cache is
 * invalidated + re-fetched, and subscribers re-render.
 *
 * Kept as a plain module (no React context) so non-React code paths
 * (e.g. left-rail row memoization) can read categories synchronously
 * without threading a provider through the component tree.
 */
import React from "react";
import { api } from "@/lib/api";


let _categories = [];
let _byId = new Map();
let _loaded = false;
let _fetching = null;
const _subscribers = new Set();


function _notify() {
  _subscribers.forEach((fn) => {
    try { fn(_categories); } catch { /* noop */ }
  });
}


export async function loadCategories(force = false) {
  if (_loaded && !force && !_fetching) return _categories;
  if (_fetching) return _fetching;
  _fetching = (async () => {
    try {
      const r = await api.get("/atlas/categories");
      _categories = r.data?.categories || [];
      _byId = new Map(_categories.map((c) => [c.id, c]));
      _loaded = true;
      _notify();
    } catch {
      _categories = [];
      _byId = new Map();
      _loaded = true;
    } finally {
      _fetching = null;
    }
    return _categories;
  })();
  return _fetching;
}


export function getCategoriesSync() {
  if (!_loaded) {
    // Kick a background fetch so subscribers get their first payload
    // shortly. Callers reading sync during first paint see [].
    loadCategories();
  }
  return _categories;
}


export function getCategoryById(id) {
  if (!id) return null;
  return _byId.get(id) || null;
}


export function subscribeCategories(fn) {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}


export function invalidateCategories() {
  _loaded = false;
  return loadCategories(true);
}


/**
 * React hook. Returns the current categories array + auto-re-renders
 * on cache invalidation. Never suspends — first render sees [] until
 * the initial fetch resolves.
 */
export function useAtlasCategories() {
  const [cats, setCats] = React.useState(_categories);
  React.useEffect(() => {
    if (!_loaded) loadCategories();
    const unsub = subscribeCategories(setCats);
    return unsub;
  }, []);
  return cats;
}
